import { useEffect, useMemo, useState } from 'react'

import { AccountBar } from '@/components/AccountBar'
import { AccountPanel } from '@/components/AccountPanel'
import { ChartGrid } from '@/components/ChartGrid'
import { InversionNotice } from '@/components/InversionNotice'
import { PlanPanel } from '@/components/PlanPanel'
import { SendOrderPanel } from '@/components/SendOrderPanel'
import { SymbolBar, quickSymbols } from '@/components/SymbolBar'
import { SymbolPicker } from '@/components/SymbolPicker'
import { Card, NumberField, Notice, Segmented } from '@/components/ui'
import { useLocalState } from '@/hooks/useLocalState'
import { useMarket } from '@/hooks/useMarket'
import { usePlan } from '@/hooks/usePlan'
import { api } from '@/lib/api'
import { parseDecimalInput } from '@/lib/numbers'
import { heldBySymbol, targetsEngaged } from '@/lib/positions'
import type {
  AccountIn,
  Instrument,
  PlanRequest,
  Position,
  Side,
  SizingBasis,
  SizingMode,
} from '@/lib/types'

/**
 * Écran du mode CALCULATEUR.
 *
 * Aucun ordre n'est envoyé nulle part : cet écran calcule et affiche l'ordre
 * inversé pour que l'utilisateur le passe lui-même. Il fonctionne
 * intégralement sans identifiants cTrader — c'est sa raison d'être (§7).
 *
 * Les cotations sont saisies à la main, faute de flux de prix. Le backend dit
 * lesquelles sont nécessaires (une ou deux selon la paire et la devise du
 * compte) plutôt que de laisser l'utilisateur deviner.
 */
export function CalculatorScreen({ positions = [] }: { positions?: Position[] }) {
  // Positions regroupées par paire : la barre rapide et le sélecteur les
  // marquent, pour qu'on sache toujours où l'on est engagé.
  const held = useMemo(() => heldBySymbol(positions), [positions])

  const [instruments, setInstruments] = useState<Instrument[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  // Un sélecteur ouvert dans la grille de graphiques compte autant que celui
  // du ticket : il affiche les mêmes prix, il lui faut les mêmes abonnements.
  const [paneBrowsing, setPaneBrowsing] = useState(false)
  const browsing = pickerOpen || paneBrowsing

  const [symbol, setSymbol] = useLocalState('symbol', 'EURUSD')
  /** Dernières paires choisies, la plus récente en tête. */
  const [recents, setRecents] = useLocalState<string[]>('recentSymbols', [])
  const [favourites, setFavourites] = useLocalState<string[]>('favourites', [])
  const [side, setSide] = useLocalState<Side>('side', 'BUY')
  const [sizing, setSizing] = useLocalState<SizingMode>('sizing', 'lots')
  /*
   * Sur quel stop porte le montant risqué.
   *
   * `platform` par défaut : je raisonne sur MON stop, comme sur n'importe
   * quelle plateforme. Conséquence à garder en tête, et affichée en
   * permanence sous les champs : l'ordre partant inversé, la perte réellement
   * possible sur le compte porte sur le TP saisi. Avec un SL de 20 et un TP de
   * 40, demander 100 € revient à pouvoir en perdre 200.
   */
  const [basis, setBasis] = useLocalState<SizingBasis>('sizingBasis', 'platform')
  const [lots, setLots] = useLocalState('lots', '0.10')
  const [riskAmount, setRiskAmount] = useLocalState('riskAmount', '100')
  const [riskPercent, setRiskPercent] = useLocalState('riskPercent', '1')
  const [stopLoss, setStopLoss] = useLocalState('stopLoss', '20')
  const [takeProfit, setTakeProfit] = useLocalState('takeProfit', '40')
  const [account, setAccount] = useLocalState<AccountIn>('account', {
    currency: 'EUR',
    balance: '10000',
    leverage: 30,
  })
  // Prix saisis, conservés d'une paire à l'autre : revenir sur EURUSD ne doit
  // pas obliger à retaper son prix.
  const [prices, setPrices] = useLocalState<Record<string, string>>('prices', {})
  const [neededQuotes, setNeededQuotes] = useState<string[]>([])
  /*
   * Plafond du ratio gain/risque, lu sur le serveur.
   *
   * Il n'est PAS écrit en dur ici : c'est un garde-fou, sa valeur vit dans le
   * .env et le serveur la fait respecter. L'interface la reprend seulement
   * pour empêcher le geste — faire glisser une cible au-delà — plutôt que de
   * laisser composer un ordre qui sera refusé.
   */
  const [maxRewardRatio, setMaxRewardRatio] = useState(2)
  /*
   * Plafond du TOTAL en jeu si toutes mes cibles étaient atteintes. 0 : aucun.
   *
   * Le serveur l'applique de toute façon à l'envoi — c'est lui qui fait foi.
   * L'écran le connaît pour pouvoir le dire AVANT, plutôt que de laisser
   * composer un ordre qui sera refusé.
   */
  const [maxTargetTotal, setMaxTargetTotal] = useState(0)
  /*
   * Repli du détail sur téléphone.
   *
   * Sous le graphique s'empilaient quatre cartes — dimensionnement, les deux
   * lectures de l'ordre, l'envoi, le rappel d'inversion — soit plus de deux
   * écrans de défilement, alors que tout s'y règle désormais sur le graphique
   * lui-même. Elles restent à un appui, et le résumé porte les deux seuls
   * chiffres qu'on ne peut pas se permettre de perdre de vue : le capital sur
   * lequel on calcule, et ce que le compte peut réellement perdre.
   *
   * Sur ordinateur la colonne est toujours là : la place ne manque pas, et
   * elle ne masque rien.
   */
  const [detailOpen, setDetailOpen] = useLocalState('detailOpen', false)

  useEffect(() => {
    api
      .config()
      .then((config) => {
        setMaxRewardRatio(config.risk_max_reward_ratio)
        setMaxTargetTotal(config.risk_max_target_total)
      })
      .catch(() => setMaxRewardRatio(2))
  }, [])

  // --- Chargement de l'univers d'instruments -------------------------------
  useEffect(() => {
    api
      .instruments()
      .then((response) => {
        setInstruments(response.instruments)
        setLoadError(null)
      })
      .catch((error: unknown) =>
        setLoadError(error instanceof Error ? error.message : 'Erreur inconnue'),
      )
  }, [])

  const instrument = instruments.find((item) => item.name === symbol)

  /** Change la paire du ticket et la retient parmi les récentes. */
  function chooseSymbol(name: string) {
    setSymbol(name)
    setRecents((current) => [name, ...current.filter((item) => item !== name)].slice(0, 8))
  }

  /*
   * Flux temps réel. On s'abonne au symbole affiché, aux paires nécessaires à
   * la conversion vers la devise du compte, et à celles de la barre rapide —
   * qui affiche leur prix.
   *
   * Pendant qu'un sélecteur est ouvert, on prend TOUTES les paires : sa liste
   * montre prix et spread, et une liste de tirets ne sert à rien. L'abonnement
   * revient à l'essentiel dès qu'il se referme — diffuser les 28 paires en
   * permanence réveillerait la radio du téléphone pour des prix que personne
   * ne regarde.
   */
  const watched = useMemo(
    () =>
      Array.from(
        new Set([
          symbol,
          ...neededQuotes,
          ...quickSymbols(instruments, favourites, held),
          ...(browsing ? instruments.filter((item) => item.enabled).map((item) => item.name) : []),
        ]),
      ),
    [symbol, neededQuotes, instruments, favourites, held, browsing],
  )
  const market = useMarket(watched)

  /*
   * Devise qui fait foi : celle du compte du courtier dès qu'il est connecté,
   * et la saisie manuelle seulement à défaut.
   *
   * L'écran réclamait les cotations de conversion vers SA devise — l'euro par
   * défaut — pendant que le serveur, lui, calcule toujours dans la devise du
   * compte réel. Sur un compte en dollars, CHFJPY demandait donc USDJPY et
   * USDCHF, que l'écran n'envoyait jamais : l'ordre restait « impossible à
   * convertir », sans rien qui indique laquelle des deux devises était fausse.
   * EURUSD et USDCHF, eux, passaient : leur conversion ne réclame aucune paire
   * tierce, ce qui a caché le défaut jusqu'à la première paire croisée.
   */
  const currency = market.account?.currency ?? account.currency

  // --- Quels prix faut-il saisir pour cette paire ? ------------------------
  useEffect(() => {
    let cancelled = false
    api
      .requiredQuotes(symbol, currency)
      .then((needed) => {
        if (!cancelled) setNeededQuotes(needed)
      })
      .catch(() => {
        if (!cancelled) setNeededQuotes([])
      })
    return () => {
      cancelled = true
    }
  }, [symbol, currency])

  // --- Construction de la requête ------------------------------------------
  const request: PlanRequest | null = useMemo(() => {
    const balance = parseDecimalInput(account.balance, { allowZero: true })
    if (balance === null) return null

    // Priorité aux cotations du broker : une saisie manuelle périmée
    // donnerait un risque faux sans que rien ne le signale.
    const quotes = neededQuotes
      .map((name) => {
        const live = market.quotes[name]
        if (live) return { symbol: name, bid: live.bid, ask: live.ask }
        const manual = parseDecimalInput(prices[name] ?? '')
        return manual === null ? null : { symbol: name, price: manual }
      })
      .filter((quote): quote is NonNullable<typeof quote> => quote !== null)

    const base = {
      symbol,
      side,
      stop_loss_pips: parseDecimalInput(stopLoss),
      take_profit_pips: parseDecimalInput(takeProfit),
      quotes,
      // Quand le courtier est là, c'est SON compte qui part — et c'est lui que
      // le serveur retient de toute façon. L'envoyer explicitement évite que
      // la requête et la réponse racontent deux histoires différentes.
      account: market.account
        ? {
            currency: market.account.currency,
            balance: market.account.balance,
            leverage: market.account.leverage,
          }
        : { ...account, balance },
      sizing_basis: basis,
    }

    // Exactement une des trois entrées de dimensionnement, sinon le backend
    // refuse la requête (et il a raison : deux sources contradictoires).
    if (sizing === 'lots') {
      const value = parseDecimalInput(lots)
      return value === null ? null : { ...base, lots: value }
    }
    if (sizing === 'amount') {
      const value = parseDecimalInput(riskAmount)
      return value === null ? null : { ...base, risk_amount: value }
    }
    const value = parseDecimalInput(riskPercent)
    return value === null ? null : { ...base, risk_percent: value }
  }, [
    symbol,
    side,
    sizing,
    basis,
    lots,
    riskAmount,
    riskPercent,
    stopLoss,
    takeProfit,
    account,
    prices,
    neededQuotes,
    market.quotes,
    market.account,
  ])

  const { plan, error, loading } = usePlan(request)

  /*
   * Ce que le compte perdrait si TOUTES mes cibles actuelles étaient
   * atteintes. `null` dès qu'une position n'est pas chiffrable : un total
   * partiel se lirait comme un total, et rassurerait à tort.
   */
  const engage = useMemo(() => targetsEngaged(positions), [positions])

  /** Refus prononcé par l'écran, avant même d'appuyer. */
  const refusCumul = useMemo(() => {
    if (!maxTargetTotal) return null
    if (engage === null) {
      return "Une position ouverte n'a pas de stop chez le courtier : le total en jeu n'est pas mesurable."
    }
    const ajoute = plan?.risk_amount ? Number(plan.risk_amount) : null
    if (ajoute === null) return null
    const cumul = engage + ajoute
    if (cumul <= maxTargetTotal) return null
    return `Cibles cumulées ${cumul.toFixed(0)} ${currency} > plafond ${maxTargetTotal.toFixed(0)} ${currency}`
  }, [maxTargetTotal, engage, plan, currency])

  // Valeurs recalculées, affichées dans les champs non pilotés (§5 :
  // « les deux champs se recalculent l'un l'autre en temps réel »).
  const computedLots = plan?.volume.lots ?? ''
  // Le champ « Risque » doit relire ce qu'on y a tapé : sur la base
  // plateforme, le montant en jeu sur MON stop est, dans l'ordre envoyé, un
  // gain — d'où `reward_amount`. Afficher `risk_amount` ici renverrait le
  // double de ce qu'on vient de saisir.
  const onPlatform = basis === 'platform'
  const computedAmount = (onPlatform ? plan?.reward_amount : plan?.risk_amount) ?? ''
  const computedPercent =
    (onPlatform ? plan?.reward_percent_of_balance : plan?.risk_percent_of_balance) ?? ''

  const missingPrices = plan?.missing_quotes ?? []

  // Le volume est arrondi au pas du broker, toujours vers le bas : le risque
  // effectif est donc légèrement inférieur à la consigne. L'écart est petit,
  // mais laisser « 100 » dans le champ sans rien dire ferait croire à un
  // risque de 100 € alors qu'il est de 98,18 €.
  const sizingGap = (() => {
    const effective = onPlatform ? plan?.reward_amount : plan?.risk_amount
    if (sizing === 'lots' || !effective) return false
    const target =
      sizing === 'amount'
        ? Number(parseDecimalInput(riskAmount) ?? 0)
        : (Number(parseDecimalInput(riskPercent) ?? 0) *
            Number(parseDecimalInput(account.balance, { allowZero: true }) ?? 0)) /
          100
    if (target <= 0) return false
    return Math.abs(target - Number(effective)) >= 0.01
  })()

  function toggleFavourite(name: string) {
    setFavourites((current) =>
      current.includes(name) ? current.filter((item) => item !== name) : [...current, name],
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {loadError && <Notice tone="error" title="Backend injoignable" items={[loadError]} />}

      {/* Sous le contenu sur téléphone, au-dessus sur ordinateur : le solde se
          surveille du coin de l'œil, il n'a pas à occuper le premier écran. */}
      <div className="order-last md:order-none">
        <AccountBar
          account={market.account}
          live={market.ready}
          fallbackCurrency={account.currency}
          fallbackBalance={account.balance}
        />
      </div>

      {/*
        Deux colonnes dès 1280 px : le graphique à gauche, le ticket à droite.
        En dessous, la grille retombe d'elle-même sur une seule colonne et
        l'ordre du DOM fait le reste — graphique d'abord, saisie ensuite, ce
        qui est l'ordre dans lequel on travaille sur un téléphone.

        `min-w-0` sur la colonne du graphique : sans lui, le canevas impose sa
        largeur intrinsèque et déborde de la grille au lieu de se réduire.
      */}
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_400px] xl:items-start">
        <div className="flex min-w-0 flex-col gap-3">

          <ChartGrid
            instruments={instruments}
            favourites={favourites}
            ticketSymbol={symbol}
            quotes={market.quotes}
            quoteAge={market.quoteAge}
            brokerReady={market.ready}
            side={side}
            stopLossPips={stopLoss}
            takeProfitPips={takeProfit}
            riskAmount={plan?.risk_amount ?? null}
            rewardAmount={plan?.reward_amount ?? null}
            currency={currency}
            onChangeStop={setStopLoss}
            onChangeTarget={setTakeProfit}
            onChangeSide={setSide}
            maxRewardRatio={maxRewardRatio}
            orderRefusal={refusCumul}
            request={request}
            plan={plan}
            onSelectTicketSymbol={chooseSymbol}
            held={held}
            recents={recents}
            onBrowsingChange={setPaneBrowsing}
            leading={
              <SymbolBar
                instruments={instruments}
                favourites={favourites}
                current={symbol}
                quotes={market.quotes}
                held={held}
                onSelect={chooseSymbol}
                onToggleFavourite={toggleFavourite}
                onOpenPicker={() => setPickerOpen(true)}
              />
            }
            onToggleFavourite={toggleFavourite}
            // Chaque champ montre la valeur SAISIE quand son mode pilote, et
            // la valeur RECALCULÉE sinon : c'est le dimensionnement
            // bidirectionnel du §5, exposé tel quel sur le graphique.
            sizingMode={sizing}
            onChangeSizingMode={setSizing}
            lots={sizing === 'lots' ? lots : computedLots}
            onChangeLots={setLots}
            riskAmountInput={sizing === 'amount' ? riskAmount : computedAmount}
            onChangeRiskAmount={setRiskAmount}
            riskPercentInput={sizing === 'percent' ? riskPercent : computedPercent}
            onChangeRiskPercent={setRiskPercent}
          />

          {/* Dépliage du détail. Téléphone et tablette uniquement. */}
          <button
            type="button"
            onClick={() => setDetailOpen(!detailOpen)}
            aria-expanded={detailOpen}
            className="flex min-h-touch items-center justify-between gap-3 rounded-lg border border-border/70 bg-panel px-3 py-2 text-left xl:hidden"
          >
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold text-ink">
                Détail du calcul et envoi
              </span>
              <span className="num block truncate text-[11px] text-ink-muted">
                {plan ? (
                  <>
                    capital {plan.account_balance} {plan.account_currency}
                    {plan.risk_amount && (
                      <>
                        {' · perte réelle '}
                        <span className="text-down">
                          −{plan.risk_amount} {plan.account_currency}
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  'calcul en cours…'
                )}
              </span>
            </span>
            <span className="shrink-0 text-[13px] text-ink-muted">
              {detailOpen ? 'masquer ▴' : 'voir ▾'}
            </span>
          </button>
        </div>

        {/*
          Colonne de saisie. Elle reste à l'écran pendant qu'on fait défiler le
          graphique — sur un écran large, devoir remonter pour changer le
          volume est le genre de détail qui fait renoncer à l'outil. Elle
          défile dans son propre cadre si elle dépasse la hauteur de la
          fenêtre, sinon le `sticky` se briserait.
        */}
        <aside
          // `[&>*]:shrink-0` : dans une colonne flex à hauteur maximale, les
          // enfants se compriment au lieu de déborder. Les cartes se
          // retrouvaient écrasées les unes sur les autres — illisibles — au
          // lieu de faire défiler la colonne.
          className={`flex-col gap-3 [&>*]:shrink-0 xl:sticky xl:top-[104px] xl:flex xl:max-h-[calc(100vh-120px)] xl:overflow-y-auto xl:pr-0.5 ${
            detailOpen ? 'flex' : 'hidden'
          }`}
        >
          {!market.ready && <AccountPanel account={account} onChange={setAccount} />}

          {/*
            L'instrument et le ticket ont disparu de cet écran : tout s'y règle
            désormais sur le graphique — sens, volume, stop et cible. Ne
            restent ici que les trois informations qui n'ont pas d'autre
            domicile, et dont aucune ne peut être perdue : sur quel capital on
            calcule, sur quel stop porte le montant, et ce que le compte peut
            réellement perdre.
          */}
          <Card title="Dimensionnement">
            <div className="flex flex-col gap-3 p-3">
              {plan && (
                <p className="text-[11px] leading-relaxed text-ink-muted">
                  Calculé sur un capital de{' '}
                  <span className="num font-semibold text-ink">
                    {plan.account_balance} {plan.account_currency}
                  </span>{' '}
                  {plan.account_source === 'broker' ? (
                    <span className="text-up">— compte du courtier</span>
                  ) : (
                    <span className="text-warn">— saisi à la main, aucun courtier connecté</span>
                  )}
                  .
                </p>
              )}

              {sizing !== 'lots' && (
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-wide text-ink-muted">
                    Ce montant porte sur
                  </p>
                  <Segmented<SizingBasis>
                    value={basis}
                    onChange={setBasis}
                    options={[
                      { value: 'platform', label: 'Mon stop' },
                      { value: 'broker', label: 'Le stop réel' },
                    ]}
                  />
                </div>
              )}

              {onPlatform && plan?.risk_amount && (
                <p className="rounded-md border border-down/40 bg-down/10 p-2 text-[11px] leading-relaxed text-ink-muted">
                  Perte réelle possible sur le compte :{' '}
                  <span className="num font-semibold text-down">
                    −{plan.risk_amount} {currency}
                  </span>
                  {plan.risk_percent_of_balance && (
                    <span className="num"> ({plan.risk_percent_of_balance} % du capital)</span>
                  )}
                  . Elle porte sur le stop de l'ordre envoyé — le Take Profit saisi — et non
                  sur le mien.
                </p>
              )}

              {/*
                Le volume est arrondi au pas du courtier, toujours vers le bas :
                le risque effectif est donc un peu inférieur à la consigne.
                Laisser « 100 » affiché sans rien dire ferait croire à un
                risque de 100 € alors qu'il est de 98,18 €.
              */}
              {sizingGap && (
                <p className="text-[11px] leading-relaxed text-warn">
                  Volume arrondi au pas du courtier : le montant effectif est de{' '}
                  <span className="num">
                    {computedAmount} {currency}
                  </span>
                  , pas exactement la consigne saisie.
                </p>
              )}
            </div>
          </Card>

          {/*
            La saisie manuelle des prix ne sert QUE sans courtier : c'est le
            mode calculateur du §7, qui doit rester utilisable sans le moindre
            identifiant. Avec un courtier connecté, les prix viennent de lui.
          */}
          {!market.ready && (
            <Card
            title="Instrument"
            action={
              instrument && (
                <span className="num text-[11px] text-ink-muted">
                  pip {instrument.pip_size} · pas {instrument.step_lots} lot
                </span>
              )
            }
          >
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="flex min-h-touch w-full items-center justify-between px-3 py-2 text-left hover:bg-elevated/70 active:bg-elevated"
            >
              <span className="num text-[18px] font-semibold text-ink-strong">
                {symbol}
              </span>
              <span className="text-[12px] text-ink-muted">changer ▾</span>
            </button>

            <div className="border-t border-border p-3">
              <p className="mb-2 text-[11px] leading-relaxed text-ink-muted">
                {market.ready
                  ? `Prix fournis par le broker en temps réel, pour la conversion vers ${currency}.`
                  : `Sans connexion broker, les prix sont saisis à la main. Ceux demandés ci-dessous servent à convertir vers ${currency}.`}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {neededQuotes.map((name) => {
                  const live = market.quotes[name]
                  return live ? (
                    <div
                      key={name}
                      className="rounded-md border border-border bg-elevated px-3 py-2"
                    >
                      <p className="text-[11px] uppercase tracking-wide text-ink-muted">
                        {name}
                      </p>
                      <p className="num text-[15px] text-ink">{live.mid}</p>
                      <p className="text-[10px] text-up">temps réel</p>
                    </div>
                  ) : (
                    <NumberField
                      key={name}
                      label={`Prix ${name}`}
                      value={prices[name] ?? ''}
                      placeholder="0.00000"
                      tone={missingPrices.includes(name) ? 'accent' : 'normal'}
                      onChange={(value) => setPrices({ ...prices, [name]: value })}
                    />
                  )
                })}
              </div>
              {missingPrices.length > 0 && (
                <p className="mt-2 text-[11px] text-warn">
                  Prix manquant{missingPrices.length > 1 ? 's' : ''} :{' '}
                  <span className="num">{missingPrices.join(', ')}</span> — le calcul
                  reprendra dès qu'ils seront renseignés.
                </p>
              )}
            </div>
          </Card>
          )}

          {/* --- Résultat ---------------------------------------------------- */}
          {error && <Notice tone="error" title="Calcul impossible" items={[error]} />}

          {plan ? (
            <div
              className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}
            >
              <PlanPanel plan={plan} />
              <div className="mt-3">
                <SendOrderPanel
                  request={request}
                  plan={plan}
                  brokerReady={market.ready}
                  currency={currency}
                />
              </div>
            </div>
          ) : (
            !error && (
              <Card>
                <p className="px-3 py-6 text-center text-[13px] text-ink-muted">
                  {request === null
                    ? 'Compléter la saisie pour voir l’ordre inversé.'
                    : 'Calcul en cours…'}
                </p>
              </Card>
            )
          )}

          <InversionNotice />
        </aside>
      </div>

      {pickerOpen && (
        <SymbolPicker
          instruments={instruments}
          value={symbol}
          favourites={favourites}
          quotes={market.quotes}
          held={held}
          recents={recents}
          onSelect={chooseSymbol}
          onToggleFavourite={toggleFavourite}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  )
}
